import _ from "lodash";
import {
  getBenchmarkSetOfCurriculumQuery,
  getBenchmarkSetQuery,
} from "modules/CommonQuery";
import { getSettingValue } from "modules/PermissionModule";
import {
  BLANK_OBJECT,
  getStandardSearchResults,
} from "Post/modules/PostModule";
import { getTaggableElements } from "modules/TaggableElementsUtils";
import {
  fetchAssessmentQuery,
  fetchLEQuery,
  getUnitPlanQuery,
  getMultiNodeUnitElementsQuery,
  getStandardSetsQuery,
  getLearningCourseStandardsQuery,
  getPlannerElementNodeSetIdsOfCurriculumQuery,
  getPlannerElementNodeSetsFromIdsQuery,
} from "Post/modules/PostQuery";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import I18nHOC from "UIComponents/I18nHOC";
import withLoader from "UIComponents/GqlWithLoader";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import getCurriculumIdsFilterForTags from "Utils/getCurriculumIdsFilterForTags";
import TagLearningGoalsList from "./TagLearningGoalsList";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_PYP,
  ELEMENT_TYPE_PYP_ACTION,
  ELEMENT_TYPE_PYP_ATL,
  ELEMENT_TYPE_PYP_KEY_CONCEPT,
  ELEMENT_TYPE_PYP_LEARNER_PROFILE,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  NODE_TYPE_IB_PYP_CURRICULUM_PROGRAM,
} from "Constants/stringConstants";
import ACLStore from "lib/aclStore";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import {
  getMultipleUnitPlanFields,
  getMultipleUnitPlanSubjects,
  getIsNewGoalTaggingEnabled,
  isAssessmentTagged,
  isUnitPlanTagged,
  getFilteredStandardSetsForTagging,
  getIsNewGradingSetupEnabled,
  getIsStandardsRevampEnabled,
  isUnitPlanOrAssessmentTagged,
  getCurrentPlannerElementSetFromState,
  getShowNewStandardsUIInV1,
} from "Post/utils";
import { getOrganizationSubjectsGradesAndTagsQuery } from "AppComponents/StandardsSelectionModal/modules/StandardsSelectionModalQueries";
import { getSelectedLearningCourseId } from "Teacher/routes/LearningCourse/modules/Utils";
import { getIsSubjectStandard } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/utils";
import { DEFAULT_STANDARD_FILTERS } from "../Utils";
import { getIsSubjectStandardsFilterEnabled } from "../../../../../../utils";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import { STANDARD_UIDS } from "modules/Services/ServicesConstants";
import {
  plannerElementFieldUidMap,
  pypElementMapping,
} from "Constants/plannerElementMappings";
import { getDatasetsOrderByArgs } from "Datasets/modules/Utils";

const getPublishedSubjectStandardQueryVariables = params => {
  const {
    id,
    searchText = "",
    standardFilters = DEFAULT_STANDARD_FILTERS,
    standardSetIds,
    curriculumProgramType,
  } = params;

  return {
    id,
    filters: {
      searchText,
      standardSetIds,
      ...standardFilters,
    },
    isPYPCurriculum: curriculumProgramType
      ? curriculumProgramType === CURRICULUM_TYPE_PYP
      : undefined,
  };
};

const BLANK_ARRAY = [];

const getPypPlannerElementNodeSetTypes = ({
  taggableElements,
  enableActioninPYP,
}) => {
  const types = [];
  _.forEach(taggableElements, element => {
    if (element.value == "benchmarks") return;
    if (element.value == "action" && !enableActioninPYP) return;
    if (element.value == "concepts") {
      types.push(ELEMENT_TYPE_PYP_KEY_CONCEPT);
      return;
    }
    if (element.value == "atls") {
      types.push(ELEMENT_TYPE_PYP_ATL);
      return;
    }
    if (element.value == "lp") {
      types.push(ELEMENT_TYPE_PYP_LEARNER_PROFILE);
      return;
    }
    if (element.value == "action") {
      types.push(ELEMENT_TYPE_PYP_ACTION);
      return;
    }
    if (element.elementType) types.push(element.elementType);
  });
  return _.uniq(types);
};

const MYP_OBJECTIVES_TYPE = "MYP_OBJECTIVES";

const NON_PYP_SKIPPED_TYPES = [
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
];

const filterNonPypTypes = types =>
  _.filter(types, type => !_.includes(NON_PYP_SKIPPED_TYPES, type));

const getNonPypNodeSetTypes = ({
  plannerElementTypes,
  subjectPlannerElementTypes,
  subjectGradePlannerElementTypes,
  gradePlannerElementTypes,
}) =>
  _.uniq(
    _.compact([
      ...filterNonPypTypes(plannerElementTypes),
      ...filterNonPypTypes(subjectPlannerElementTypes),
      ...filterNonPypTypes(subjectGradePlannerElementTypes),
      ...filterNonPypTypes(gradePlannerElementTypes),
      MYP_OBJECTIVES_TYPE,
    ])
  );

const toPlannerElementNodeFilters = filters => {
  if (!filters) return undefined;
  const { associatedParents, genericTags: _gt, ...rest } = filters;
  const result = { ...rest };
  _.forEach(associatedParents, ({ type, ids }) => {
    if (_.isEmpty(ids)) return;
    if (type === "SUBJECT") result.subjects = ids;
    if (type === "GRADE") result.grades = ids;
  });
  return _.isEmpty(result) ? undefined : result;
};

const buildAssociatedParentsFromProperties = properties => [
  ..._.map(_.get(properties, "subjects"), item => ({
    __typename: "Subject",
    id: item.id,
    name: item.name,
    isArchived: item.isArchived,
  })),
  ..._.map(_.get(properties, "grades"), item => ({
    __typename: "Grade",
    id: item.id,
    name: item.name,
  })),
  ..._.map(_.get(properties, "subjectGroups"), item => ({
    __typename: "SubjectGroup",
    id: item.id,
    name: item.name,
  })),
  ..._.map(_.get(properties, "associatedPlannerElementNodes"), item => ({
    __typename: "PlannerElementNode",
    id: item.id,
    label: item.label,
    type: item.type,
    metadata: item.metadata,
  })),
];

const mapNodeSetEdgesToNodes = edges =>
  _.map(edges, edge => {
    const node = _.get(edge, "node") || {};
    const properties = _.get(node, "dataSetNodeWrapper.properties");
    return {
      ...node,
      associatedParents: buildAssociatedParentsFromProperties(properties),
      tags: _.get(properties, "tags"),
    };
  });

const mapNodeSetsToPlannerElementSets = ({ nodeSets, subtypes }) =>
  _.map(nodeSets, nodeSet => ({
    id: _.get(nodeSet, "plannerElementSet.id", _.get(nodeSet, "id")),
    label: _.get(nodeSet, "plannerElementSet.label"),
    type: _.get(nodeSet, "type"),
    nodes: mapNodeSetEdgesToNodes(
      _.get(
        nodeSet,
        "plannerElementNodeSetWrapperV2.publishedData.edges",
        BLANK_ARRAY
      )
    ),
    ...(subtypes ? { subtypes } : {}),
  }));

const getActionObj = t => {
  return {
    id: "action",
    uid: "action",
    resolvedMinimalTree: {
      id: "action",
      type: "ACTION",
      action: [
        {
          id: "action",
          label: t("common:action_label"),
        },
      ],
    },
  };
};

const plannerElementQueryVariables = ({
  courseId,
  taggableElements,
  academicYearId,
  standardsTagFilter,
}) => {
  const plannerElementTypes = [];
  const subjectPlannerElementTypes = [];
  const subjectGradePlannerElementTypes = [];
  const gradePlannerElementTypes = [];
  let subjectPlannerElementFilters = undefined;
  let subjectGradePlannerElementFilters = undefined;
  let gradePlannerElementFilters = undefined;
  const isSubjectStandardFilterEnabled = getIsSubjectStandardsFilterEnabled();
  const courseDetails = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters: {
      academicYearId,
    },
  });
  const subjects = _.get(courseDetails, "subjects", []);
  const grades = _.get(courseDetails, "grades", []);
  _.forEach(taggableElements, element => {
    if (element.isGlobal) {
      plannerElementTypes.push(element.elementType);
    }
  });
  _.forEach(taggableElements, element => {
    if (!element.isGlobal && !element.isGradeSpecific) {
      subjectPlannerElementTypes.push(element.elementType);
    }
  });
  _.forEach(taggableElements, element => {
    if (
      !element.isGlobal &&
      element.isGradeSpecific &&
      element.isSubjectSpecific
    ) {
      subjectGradePlannerElementTypes.push(element.elementType);
    }
  });
  _.forEach(taggableElements, element => {
    if (
      !element.isGlobal &&
      element.isGradeSpecific &&
      !element.isSubjectSpecific
    ) {
      gradePlannerElementTypes.push(element.elementType);
    }
  });

  subjectPlannerElementFilters = {
    associatedParents: [
      {
        ids: _.map(subjects, item => item.id),
        type: "SUBJECT",
      },
    ],
  };
  subjectGradePlannerElementFilters = {
    associatedParents: _.filter(
      [
        {
          ids: _.map(subjects, item => item.id),
          type: "SUBJECT",
        },
        {
          ids: _.map(grades, item => item.id),
          type: "GRADE",
        },
      ],
      item => !_.isEmpty(item.ids)
    ),
    genericTags: isSubjectStandardFilterEnabled
      ? standardsTagFilter
      : undefined,
  };
  gradePlannerElementFilters = {
    associatedParents: _.filter(
      [
        {
          ids: _.map(grades, item => item.id),
          type: "GRADE",
        },
      ],
      item => !_.isEmpty(item.ids)
    ),
  };

  return {
    plannerElementTypes,
    subjectPlannerElementTypes,
    subjectGradePlannerElementTypes,
    subjectPlannerElementFilters,
    subjectGradePlannerElementFilters,
    gradePlannerElementTypes,
    gradePlannerElementFilters,
  };
};

export const getStandardEdges = params => {
  const { node, showCourseStandards, selectFromAllPYP, leValues } = params;
  if (showCourseStandards) {
    let edges = _.flatMap(
      _.get(node, "standardsWrapper.standardsEdge") ||
        _.get(node, "benchmarksWrapper.standardsEdge"),
      edge =>
        _.map(edge.standards || [], standard => ({
          node: standard,
          standardSetId: _.get(edge, "standardSet.id"),
        }))
    );
    if (!selectFromAllPYP && !_.isEmpty(leValues)) {
      edges = _.filter(edges, edge =>
        _.includes(leValues, _.get(edge, "node.id"))
      );
    }
    const groupedByStandardSetId = _.groupBy(edges, "standardSetId");
    return groupedByStandardSetId;
  } else {
    const edges =
      _.get(node, "standardSetWrapper.publishedStandards.edges") ||
      _.get(node, "standardSetWrapper.publishedBenchmarks.edges") ||
      [];
    return edges;
  }
};

const plannerElementQueryVariablesMemoize = _.memoize(
  plannerElementQueryVariables,
  params => {
    const {
      courseId,
      taggableElements,
      academicYearId,
      standardsTagFilter,
    } = params;

    // Create hash for taggable elements using only elementType
    const taggableElementsHash = _.map(
      taggableElements,
      element => element.elementType
    ).join("|");

    // Create hash for standards tag filter (assuming it's always an array)
    const standardsTagFilterHash = (standardsTagFilter || []).join(",");

    return JSON.stringify(
      `${courseId}_${academicYearId}_${taggableElementsHash}_${standardsTagFilterHash}`
    );
  }
);

const getLeValues = ({ fields, fieldUid }) => {
  const leValues = [];
  const standardFields = _.filter(
    fields,
    field => _.get(field, "uid") === fieldUid
  );
  _.forEach(standardFields, field => {
    leValues.push(..._.defaultTo(_.get(field, "value"), []));
  });
  return _.uniq(leValues);
};
const mapActionCreators = {
  getStandardSearchResults,
};
const mapStateToProps = (state, ownProps) => {
  const organizationId = state?.login?.userInfo?.org_id;
  const selectedGrades =
    state.teacher?.selected_class?.selected_grades || [
      _.get(state, "post.postDetails.course.grade.id", ""),
    ] ||
    [];
  const curriculumType = _.get(
    state,
    "platform.currentCurriculumProgram.type",
    "IB_PYP"
  );
  const isSubjectStandardFilterEnabled = getIsSubjectStandardsFilterEnabled();
  const academicYearId = getSelectedAcademicYearId(state);

  // For multi-curriculum support, we have to use programType and ID from "currentCurriculumProgram"
  const currentCurriculumProgramType = _.get(
    state,
    "platform.currentCurriculumProgram.type",
    ""
  );
  const currentCurriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id",
    undefined
  );

  const standardSetIdFromProps = _.get(
    state,
    "post.postDetails.standardSetIds",
    BLANK_ARRAY
  );
  const {
    resourceItem,
    unitPlan,
    resourceType,
    selectFromAllPYP,
    elementPYP,
    postTaggedEntity,
  } = state.post.postDetails;
  const standardsTagFilter = _.get(
    state,
    "post.postDetails.standardsTagFilter"
  );
  const plannerElementSets = _.get(
    state,
    "platform.currentPlannerElementSets",
    []
  );
  let USER_TAGGABLE_ELEMENTS = getTaggableElements({
    curriculumProgramType: curriculumType,
    unitPlan,
    plannerElementSets,
    postTaggedEntity,
  });
  const curriculum = _.find(state.platform.organizationCurriculumPrograms, {
    type: currentCurriculumProgramType,
    id: currentCurriculumProgramId,
  });
  const userType = state.login.userInfo.user_type;
  // show pyp elements to user based on setting
  if (userType == "student") {
    const omitPYPElements = getSettingValue({
      name: "OmittedPYPElementsForTaggingInPostByStudents",
      courseId: state.teacher.selected_class.selected_course,
      organizationId: state.login.userInfo.org_id,
      userId: state.login.userInfo.id,
      userEntityType: state.login.userInfo.userEntityType,
    });

    USER_TAGGABLE_ELEMENTS = _.filter(USER_TAGGABLE_ELEMENTS, element => {
      return (
        _.findIndex(
          omitPYPElements,
          omitElement => element.value == omitElement
        ) < 0
      );
    });
  }

  const uids = _.map(USER_TAGGABLE_ELEMENTS, element => {
    return element.value;
  });

  let plannerElementVariables = {};
  if (curriculumType != "IB_PYP") {
    plannerElementVariables = plannerElementQueryVariablesMemoize({
      courseId: state.teacher.selected_class.selected_course,
      taggableElements: USER_TAGGABLE_ELEMENTS,
      academicYearId,
      standardsTagFilter,
    });
  }

  const enableActioninPYP = ACLStore.can("FeatureFlag:EnableAction");

  if (!enableActioninPYP) {
    _.pull(uids, "action");
  }
  const isTaggedFromRecentPost = _.get(
    state,
    "post.postDetails.isTaggedFromRecentPost",
    false
  );
  const standardFilters = _.get(
    state,
    "post.postDetails.standardFilters",
    BLANK_OBJECT
  );
  const learningCourseId = getSelectedLearningCourseId(state);
  const isNewGradingSetupEnabled = getIsNewGradingSetupEnabled();
  const updatedUserTaggableElements = _.filter(
    USER_TAGGABLE_ELEMENTS,
    element =>
      !(
        ((getIsStandardsRevampEnabled() &&
          learningCourseId &&
          getIsNewGradingSetupEnabled()) ||
          (getShowNewStandardsUIInV1() && !learningCourseId)) &&
        getIsSubjectStandard({ type: element.elementType })
      )
  );

  const isLearningCourseEnabled = getIsLearningCoursesEnabled(state);
  const standardLabel = _.get(
    getCurrentPlannerElementSetFromState(
      _.get(plannerElementFieldUidMap, _.get(STANDARD_UIDS, curriculumType))
    )(state),
    "label",
    ""
  );

  const { orderBy, orderByDirection } = getDatasetsOrderByArgs();

  const commonProps = {
    uids: uids,
    isData: true,
    isLoading: false,
    showLoaderBar: false,
    taggableElements: USER_TAGGABLE_ELEMENTS,
    curriculumId: currentCurriculumProgramId,
    curriculumNodeInterfaceType: _.get(curriculum, "nodeInterfaceType", ""),
    ...plannerElementVariables,
    curriculumType: currentCurriculumProgramType,
    enableActioninPYP,
    elementPYP,
    postTaggedElements: _.get(state, "post.postDetails.postTaggedElements", []),
    selectFromPost: _.get(state, "post.postDetails.selectFromPost", false),
    isTaggedFromRecentPost,
    postTaggedEntity,
    curriculumProgramId: currentCurriculumProgramId,
    learningCourseId: learningCourseId
      ? learningCourseId
      : _.get(state, "post.postDetails.course.learningCourse[0].id"),
    showCourseStandardSets: true, // This prop is used to show course standard sets,
    showCourseStandards: true, // This prop is used to show course standards,
    standardFilters,
    isNewGradingSetupEnabled,
    standardsTagFilter,
    isSubjectStandardFilterEnabled,
    customLoadingComponent: ownProps.customLoadingComponent,
    showLoadingIcon: !getIsStandardsRevampEnabled(),
    isLearningCourseEnabled,
    standardSetIdFromProps,
    isPYPCurriculum: currentCurriculumProgramType === CURRICULUM_TYPE_PYP,
    standardLabel,
    orderBy,
    orderByDirection,
  };
  if (selectFromAllPYP) {
    return {
      ...commonProps,
      taggableElements: updatedUserTaggableElements,
      selectFromAllPYP,
      organizationId,
      selectedGrades,
      standardFilters,
      isNewGradingSetupEnabled,
    };
  }

  const isAssessment = isAssessmentTagged(postTaggedEntity);
  const isUnitPlan = isUnitPlanTagged(postTaggedEntity);
  const isNewGoalTaggingEnabled = getIsNewGoalTaggingEnabled();
  if (
    (isNewGoalTaggingEnabled
      ? !isAssessment
      : !resourceItem || !resourceType) ||
    (isNewGoalTaggingEnabled ? isUnitPlan : false)
  ) {
    return {
      id: _.get(unitPlan, "id", ""),
      ids: isAssessment
        ? _.compact([_.get(_.first(postTaggedEntity), "entity.id")])
        : _.compact(
            _.map(postTaggedEntity, postEntity =>
              _.get(postEntity, "entity.id")
            )
          ),
      parentType: "UNIT_PLAN",
      ...commonProps,
      organizationId,
    };
  } else {
    return {
      id: _.get(resourceItem, "id", ""),
      ids: isAssessment
        ? _.compact([_.get(_.first(postTaggedEntity), "entity.id")])
        : _.compact(
            _.map(postTaggedEntity, postEntity =>
              _.get(postEntity, "entity.id")
            )
          ),
      parentType: isNewGoalTaggingEnabled
        ? _.get(_.first(postTaggedEntity), "entityType", "")
        : resourceType,
      ...commonProps,
      organizationId,
    };
  }
};

const getStandardSetIds = (standardsWrapper, isPYPCurriculum) => {
  const standardEntity = _.find(standardsWrapper, field =>
    getIsSubjectStandard({ type: pypElementMapping[field.uid] })
  );
  if (
    standardEntity &&
    _.isArray(standardEntity.value) &&
    !_.isEmpty(standardEntity.value)
  ) {
    return _.map(
      isPYPCurriculum
        ? _.get(standardEntity, "resolvedMinimalTree.benchmarks", [])
        : _.get(standardEntity, "resolvedMinimalTree.nodes", []),
      "standardsWrapper.standardSet.id"
    );
  }
  return [];
};

const filterSubjectStandardsBySubjects = ({
  fields,
  subjectIds,
  elementPYP,
}) => {
  // Find all fields with uid "benchmarks"
  const benchmarksFields = _.filter(
    fields,
    field => _.get(field, "uid") === "benchmarks"
  );

  // Process each benchmarks field
  _.forEach(benchmarksFields, benchmarksField => {
    if (
      !_.isNil(benchmarksField) &&
      !_.isNil(_.get(benchmarksField, "resolvedMinimalTree.benchmarks", []))
    ) {
      const resolvedTreeBenchmarks = _.get(
        benchmarksField,
        "resolvedMinimalTree.benchmarks",
        []
      );
      const taggedBenchmarks = _.get(
        _.find(
          elementPYP,
          element => _.get(element, "fieldUID") === "benchmarks"
        ),
        "resolvedMinimalTree.benchmarks",
        []
      );

      // filter out benchmarks if their subject is deleted
      const filteredResolvedBenchmarks = _.filter(
        resolvedTreeBenchmarks,
        benchmark => _.includes(subjectIds, _.get(benchmark, "subject"))
      );

      // Find the index of this benchmarks field in the original fields array
      const fieldIndex = _.findIndex(
        fields,
        field => field.id === benchmarksField.id
      );
      if (fieldIndex !== -1) {
        fields[fieldIndex].resolvedMinimalTree.benchmarks = _.unionBy(
          filteredResolvedBenchmarks,
          taggedBenchmarks,
          "id"
        ); // add previously tagged elements of deleted subject standards as well
      }
    }
  });
};

export default compose(
  I18nHOC({ resource: ["journal", "common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getUnitPlanQuery, {
    name: "getUnitPlan",
    skip: ({ parentType, selectFromAllPYP, id }) =>
      parentType != "UNIT_PLAN" ||
      selectFromAllPYP ||
      _.isNil(id) ||
      _.isEmpty(id) ||
      getIsNewGoalTaggingEnabled(),
    options: ({ id, uids }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: id,
        uids: uids,
      },
    }),
    props({
      getUnitPlan,
      ownProps: {
        isData,
        isLoading,
        t,
        enableActioninPYP,
        elementPYP,
        curriculumType,
      },
    }) {
      const _fields = _.get(getUnitPlan, "node.fields", []);
      const fields = _.cloneDeep(_fields);
      if (
        !_.isEmpty(fields) &&
        isData &&
        !enableActioninPYP &&
        curriculumType !== CURRICULUM_TYPE_DP
      ) {
        fields.push(getActionObj(t));
      }

      const subjectIds = _.isEmpty(
        _.get(
          getUnitPlan,
          "node.unitPlan.subjects.resolvedMinimalTree.subjects",
          []
        )
      )
        ? _.chain(getUnitPlan)
            .get("node.fields", [])
            .find({ uid: "benchmarks" })
            .get("resolvedMinimalTree.benchmarks", [])
            .filter(benchmark => {
              const resolvedSubject = benchmark?.resolvedSubject;
              return (
                _.get(resolvedSubject, "isDeleted", false) === false &&
                !_.get(resolvedSubject, "isArchived", false)
              );
            })
            ?.map("subject")
            ?.compact()
            ?.uniq()
            ?.value()
        : _.reduce(
            _.get(
              getUnitPlan,
              "node.subjects.resolvedMinimalTree.subjects",
              []
            ),
            (result, subject) => {
              if (
                _.get(subject, "isDeleted", false) === false &&
                !_.get(subject, "isArchived", false)
              ) {
                return [...result, _.get(subject, "id")];
              }
              return result;
            },
            []
          );

      filterSubjectStandardsBySubjects({ fields, subjectIds, elementPYP });
      return {
        getUnitPlan,
        fields,
        isData: !_.isEmpty(fields) && isData,
        isLoading:
          getUnitPlan["networkStatus"] == 1 ||
          getUnitPlan["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(getMultiNodeUnitElementsQuery, {
    name: "getUnitPlanV2",
    skip: ({ ids, selectFromAllPYP, postTaggedEntity }) =>
      _.isEmpty(postTaggedEntity) ||
      selectFromAllPYP ||
      !getIsNewGoalTaggingEnabled() ||
      _.isEmpty(ids),
    options: ({ ids, uids, postTaggedEntity }) => ({
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      variables: {
        id: ids,
        uids: uids,
        type: _.first(postTaggedEntity)?.entityType,
      },
    }),
    props({
      getUnitPlanV2,
      ownProps: {
        isData,
        isLoading,
        t,
        enableActioninPYP,
        elementPYP,
        curriculumType,
        postTaggedEntity,
      },
    }) {
      const _fields = getMultipleUnitPlanFields(
        _.get(getUnitPlanV2, "multiNode", [])
      );
      const fields = _.cloneDeep(_fields);
      if (
        !_.isEmpty(fields) &&
        isData &&
        !enableActioninPYP &&
        curriculumType !== CURRICULUM_TYPE_DP
      ) {
        fields.push(getActionObj(t));
      }

      const subjectIds = _.isEmpty(
        getMultipleUnitPlanSubjects(
          _.get(getUnitPlanV2, "multiNode", []),
          isAssessmentTagged(postTaggedEntity)
        )
      )
        ? _.chain(_.get(_.first(getUnitPlanV2?.multiNode), "fields", []))
            .find({ uid: "benchmarks" })
            .get("resolvedMinimalTree.benchmarks", [])
            .filter(benchmark => {
              const resolvedSubject = benchmark?.resolvedSubject;
              return (
                _.get(resolvedSubject, "isDeleted", false) === false &&
                !_.get(resolvedSubject, "isArchived", false)
              );
            })
            .map("subject")
            .compact()
            .uniq()
            .value() || []
        : _.reduce(
            getMultipleUnitPlanSubjects(
              _.get(getUnitPlanV2, "multiNode", []),
              isAssessmentTagged(postTaggedEntity)
            ),
            (result, subject) => {
              if (
                _.get(subject, "isDeleted", false) === false &&
                !_.get(subject, "isArchived", false)
              ) {
                return [...result, _.get(subject, "id")];
              }
              return result;
            },
            []
          );

      filterSubjectStandardsBySubjects({ fields, subjectIds, elementPYP });
      return {
        getUnitPlanV2,
        fields,
        isData: !_.isEmpty(getUnitPlanV2.multiNode) && isData,
        isLoading:
          getUnitPlanV2["networkStatus"] == 1 ||
          getUnitPlanV2["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(fetchAssessmentQuery, {
    name: "getAssessment",
    skip: ({ parentType, selectFromAllPYP, id }) =>
      parentType != "ASSESSMENT" ||
      selectFromAllPYP ||
      _.isNil(id) ||
      _.isEmpty(id),
    options: ({ id, uids }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: id,
        uids: uids,
      },
    }),
    props({
      getAssessment,
      ownProps: { isData, isLoading, t, enableActioninPYP, elementPYP },
    }) {
      const _fields = _.get(getAssessment, "node.fields", []);
      const fields = _.cloneDeep(_fields);
      if (!_.isEmpty(fields) && isData && !enableActioninPYP) {
        fields.push(getActionObj(t));
      }
      const subjectIds = _.isEmpty(
        _.get(
          getAssessment,
          "node.unitPlan.subjects.resolvedMinimalTree.subjects",
          []
        )
      )
        ? _.chain(getAssessment)
            .get("node.fields", [])
            .find({ uid: "benchmarks" })
            .get("resolvedMinimalTree.benchmarks", [])
            .filter(benchmark => {
              const resolvedSubject = benchmark?.resolvedSubject;
              return (
                _.get(resolvedSubject, "isDeleted", false) === false &&
                !_.get(resolvedSubject, "isArchived", false)
              );
            })
            ?.map("subject")
            ?.compact()
            ?.uniq()
            ?.value()
        : _.reduce(
            _.get(
              getAssessment,
              "node.unitPlan.subjects.resolvedMinimalTree.subjects",
              []
            ),
            (result, subject) => {
              if (
                _.get(subject, "isDeleted", false) === false &&
                !_.get(subject, "isArchived", false)
              ) {
                return [...result, _.get(subject, "id")];
              }
              return result;
            },
            []
          );

      filterSubjectStandardsBySubjects({ fields, subjectIds, elementPYP });
      return {
        getAssessment,
        fields,
        isData: !_.isEmpty(fields) && isData,
        isLoading:
          getAssessment["networkStatus"] == 1 ||
          getAssessment["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(fetchLEQuery, {
    name: "getLE",
    skip: ({ parentType, selectFromAllPYP }) =>
      parentType != "LEARNING_ENGAGEMENT" || selectFromAllPYP,
    options: ({ id, uids }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: id,
        uids: uids,
      },
    }),
    props({ getLE, ownProps: { isData, isLoading, t, enableActioninPYP } }) {
      const fields = _.get(getLE, "node.fields", {});
      if (!_.isEmpty(fields) && isData && !enableActioninPYP) {
        fields.push(getActionObj(t));
      }

      return {
        getLE,
        fields,
        isData: !_.isEmpty(fields) && isData,
        isLoading:
          getLE["networkStatus"] == 1 ||
          getLE["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  // This query is skipped, we will use getBenchmarkSetOfCurriculumQuery below.
  graphql(getBenchmarkSetQuery, {
    name: "getBenchmarkSet",
    skip: ({ selectFromAllPYP, curriculumType }) =>
      true || !selectFromAllPYP || curriculumType !== CURRICULUM_TYPE_PYP,
    options: ({ selectedGrades, organizationId }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: { grades: selectedGrades, organizationId },
      };
    },
    props({ getBenchmarkSet, ownProps: { isData, isLoading } }) {
      const benchmarkData = _.get(
        getBenchmarkSet,
        "node.pypElement.benchmarks.nodes",
        []
      );
      return {
        isData: !_.isEmpty(benchmarkData) && isData,
        getBenchmarkSet,
        benchmarkData: benchmarkData,
        isLoading:
          getBenchmarkSet["networkStatus"] == 1 ||
          getBenchmarkSet["networkStatus"] == 2 ||
          getBenchmarkSet["networkStatus"] == 4 ||
          isLoading,
      };
    },
  }),
  graphql(getBenchmarkSetOfCurriculumQuery, {
    name: "getBenchmarkSetOfCurriculum",
    skip: ({ selectFromAllPYP, curriculumType }) =>
      !selectFromAllPYP ||
      curriculumType !== CURRICULUM_TYPE_PYP ||
      (selectFromAllPYP && getShowNewStandardsUIInV1()),
    options: ({ selectedGrades, standardsTagFilter, curriculumId }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          grades: selectedGrades,
          curriculumId,
          type: NODE_TYPE_IB_PYP_CURRICULUM_PROGRAM,
          genericTagIds: standardsTagFilter,
        },
      };
    },
    props({ getBenchmarkSetOfCurriculum, ownProps: { isData, isLoading } }) {
      const benchmarkData = _.get(
        getBenchmarkSetOfCurriculum,
        "node.plannerElementSets[0].nodes",
        []
      );

      return {
        isData:
          isData && !_.isEmpty(_.get(getBenchmarkSetOfCurriculum, "node")),
        getBenchmarkSetOfCurriculum,
        benchmarkData: benchmarkData,
        isLoading:
          getBenchmarkSetOfCurriculum["networkStatus"] == 1 ||
          getBenchmarkSetOfCurriculum["networkStatus"] == 2 ||
          getBenchmarkSetOfCurriculum["networkStatus"] == 4 ||
          isLoading,
      };
    },
  }),
  graphql(getPlannerElementNodeSetIdsOfCurriculumQuery, {
    name: "getNonPypPlannerElementNodeSetIds",
    skip: ({
      curriculumId,
      selectFromAllPYP,
      curriculumType,
      plannerElementTypes,
      subjectPlannerElementTypes,
      subjectGradePlannerElementTypes,
      gradePlannerElementTypes,
    }) =>
      !curriculumId ||
      !selectFromAllPYP ||
      curriculumType == CURRICULUM_TYPE_PYP ||
      _.isEmpty(
        getNonPypNodeSetTypes({
          plannerElementTypes,
          subjectPlannerElementTypes,
          subjectGradePlannerElementTypes,
          gradePlannerElementTypes,
        })
      ),
    options: ({
      curriculumId,
      curriculumNodeInterfaceType,
      plannerElementTypes,
      subjectPlannerElementTypes,
      subjectGradePlannerElementTypes,
      gradePlannerElementTypes,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: curriculumId,
        type: curriculumNodeInterfaceType,
        plannerElementNodeSetFilters: {
          types: getNonPypNodeSetTypes({
            plannerElementTypes,
            subjectPlannerElementTypes,
            subjectGradePlannerElementTypes,
            gradePlannerElementTypes,
          }),
        },
      },
    }),
    props({ getNonPypPlannerElementNodeSetIds, ownProps: { isLoading } }) {
      const nodeSets = _.map(
        _.get(
          getNonPypPlannerElementNodeSetIds,
          "node.plannerElementNodeSets.edges",
          BLANK_ARRAY
        ),
        "node"
      );
      return {
        nonPypPlannerElementNodeSets: nodeSets,
        nonPypNodeSetIdsLoading:
          _.includes(
            [1, 2],
            _.get(getNonPypPlannerElementNodeSetIds, "networkStatus")
          ) || isLoading,
      };
    },
  }),
  graphql(getPlannerElementNodeSetsFromIdsQuery, {
    name: "getInterdisciplinaryCriteriaMYPNodeSets",
    skip: ({
      nonPypPlannerElementNodeSets,
      selectFromAllPYP,
      curriculumType,
    }) =>
      !selectFromAllPYP ||
      curriculumType == CURRICULUM_TYPE_PYP ||
      _.isEmpty(
        _.filter(nonPypPlannerElementNodeSets, { type: MYP_OBJECTIVES_TYPE })
      ),
    options: ({ nonPypPlannerElementNodeSets }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        plannerElementNodeSetIds: _.map(
          _.filter(nonPypPlannerElementNodeSets, { type: MYP_OBJECTIVES_TYPE }),
          "id"
        ),
        plannerElementATLFilters: undefined,
        plannerElementNonAtlFilters: {
          subTypes: ["INTERDISCIPLINARY_CRITERIA"],
        },
      },
      context: { useHttpLink: true },
    }),
    props({ getInterdisciplinaryCriteriaMYPNodeSets, ownProps: { isData } }) {
      const nodeSets = _.get(
        getInterdisciplinaryCriteriaMYPNodeSets,
        "multiNode",
        BLANK_ARRAY
      );
      const interdisciplinaryCriteriaMYPElements = mapNodeSetsToPlannerElementSets(
        { nodeSets, subtypes: "INTERDISCIPLINARY_CRITERIA" }
      );
      return {
        interdisciplinaryCriteriaMYPElements,
        isData: !_.isEmpty(nodeSets) && isData,
        interdisciplinaryCriteriaMYPLoading: _.includes(
          [1, 2],
          _.get(getInterdisciplinaryCriteriaMYPNodeSets, "networkStatus")
        ),
      };
    },
  }),
  graphql(getPlannerElementNodeSetsFromIdsQuery, {
    name: "getGlobalPlannerElementNodeSets",
    skip: ({
      curriculumType,
      selectFromAllPYP,
      plannerElementTypes,
      nonPypPlannerElementNodeSets,
    }) =>
      !selectFromAllPYP ||
      curriculumType == CURRICULUM_TYPE_PYP ||
      _.isEmpty(plannerElementTypes) ||
      _.isEmpty(
        _.filter(nonPypPlannerElementNodeSets, set =>
          _.includes(filterNonPypTypes(plannerElementTypes), set?.type)
        )
      ),
    options: ({ nonPypPlannerElementNodeSets, plannerElementTypes }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        plannerElementNodeSetIds: _.map(
          _.filter(nonPypPlannerElementNodeSets, set =>
            _.includes(filterNonPypTypes(plannerElementTypes), set?.type)
          ),
          "id"
        ),
        plannerElementATLFilters: undefined,
        plannerElementNonAtlFilters: undefined,
      },
      context: { useHttpLink: true },
    }),
    props({ getGlobalPlannerElementNodeSets, ownProps: { isData } }) {
      const nodeSets = _.get(
        getGlobalPlannerElementNodeSets,
        "multiNode",
        BLANK_ARRAY
      );
      return {
        plannerElements: mapNodeSetsToPlannerElementSets({ nodeSets }),
        isData: !_.isEmpty(nodeSets) && isData,
        plannerElementsLoading: _.includes(
          [1, 2],
          _.get(getGlobalPlannerElementNodeSets, "networkStatus")
        ),
      };
    },
  }),
  graphql(getPlannerElementNodeSetsFromIdsQuery, {
    name: "getSubjectPlannerElementNodeSets",
    skip: ({
      curriculumType,
      selectFromAllPYP,
      subjectPlannerElementTypes,
      nonPypPlannerElementNodeSets,
    }) =>
      !selectFromAllPYP ||
      curriculumType == CURRICULUM_TYPE_PYP ||
      _.isEmpty(subjectPlannerElementTypes) ||
      _.isEmpty(
        _.filter(nonPypPlannerElementNodeSets, set =>
          _.includes(filterNonPypTypes(subjectPlannerElementTypes), set?.type)
        )
      ),
    options: ({
      nonPypPlannerElementNodeSets,
      subjectPlannerElementTypes,
      subjectPlannerElementFilters,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        plannerElementNodeSetIds: _.map(
          _.filter(nonPypPlannerElementNodeSets, set =>
            _.includes(filterNonPypTypes(subjectPlannerElementTypes), set?.type)
          ),
          "id"
        ),
        plannerElementATLFilters: undefined,
        plannerElementNonAtlFilters: toPlannerElementNodeFilters(
          subjectPlannerElementFilters
        ),
      },
      context: { useHttpLink: true },
    }),
    props({ getSubjectPlannerElementNodeSets, ownProps: { isData } }) {
      const nodeSets = _.get(
        getSubjectPlannerElementNodeSets,
        "multiNode",
        BLANK_ARRAY
      );
      return {
        subjectPlannerElements: mapNodeSetsToPlannerElementSets({ nodeSets }),
        isData: !_.isEmpty(nodeSets) && isData,
        subjectPlannerElementsLoading: _.includes(
          [1, 2],
          _.get(getSubjectPlannerElementNodeSets, "networkStatus")
        ),
      };
    },
  }),
  graphql(getPlannerElementNodeSetsFromIdsQuery, {
    name: "getSubjectGradePlannerElementNodeSets",
    skip: ({
      curriculumType,
      selectFromAllPYP,
      subjectGradePlannerElementTypes,
      isNewGradingSetupEnabled,
      learningCourseId,
      nonPypPlannerElementNodeSets,
    }) =>
      !selectFromAllPYP ||
      curriculumType == CURRICULUM_TYPE_PYP ||
      _.isEmpty(subjectGradePlannerElementTypes) ||
      (isNewGradingSetupEnabled && learningCourseId) ||
      _.isEmpty(
        _.filter(nonPypPlannerElementNodeSets, set =>
          _.includes(
            filterNonPypTypes(subjectGradePlannerElementTypes),
            set?.type
          )
        )
      ),
    options: ({
      nonPypPlannerElementNodeSets,
      subjectGradePlannerElementTypes,
      subjectGradePlannerElementFilters,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        plannerElementNodeSetIds: _.map(
          _.filter(nonPypPlannerElementNodeSets, set =>
            _.includes(
              filterNonPypTypes(subjectGradePlannerElementTypes),
              set?.type
            )
          ),
          "id"
        ),
        plannerElementATLFilters: undefined,
        plannerElementNonAtlFilters: toPlannerElementNodeFilters(
          subjectGradePlannerElementFilters
        ),
      },
      context: { useHttpLink: true },
    }),
    props({ getSubjectGradePlannerElementNodeSets, ownProps: { isData } }) {
      const nodeSets = _.get(
        getSubjectGradePlannerElementNodeSets,
        "multiNode",
        BLANK_ARRAY
      );
      return {
        subjectGradePlannerElements: mapNodeSetsToPlannerElementSets({
          nodeSets,
        }),
        isData: !_.isEmpty(nodeSets) && isData,
        subjectGradePlannerElementsLoading: _.includes(
          [1, 2],
          _.get(getSubjectGradePlannerElementNodeSets, "networkStatus")
        ),
      };
    },
  }),
  graphql(getPlannerElementNodeSetsFromIdsQuery, {
    name: "getGradePlannerElementNodeSets",
    skip: ({
      curriculumType,
      selectFromAllPYP,
      gradePlannerElementTypes,
      nonPypPlannerElementNodeSets,
    }) =>
      !selectFromAllPYP ||
      curriculumType == CURRICULUM_TYPE_PYP ||
      _.isEmpty(gradePlannerElementTypes) ||
      _.isEmpty(
        _.filter(nonPypPlannerElementNodeSets, set =>
          _.includes(filterNonPypTypes(gradePlannerElementTypes), set?.type)
        )
      ),
    options: ({
      nonPypPlannerElementNodeSets,
      gradePlannerElementTypes,
      gradePlannerElementFilters,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        plannerElementNodeSetIds: _.map(
          _.filter(nonPypPlannerElementNodeSets, set =>
            _.includes(filterNonPypTypes(gradePlannerElementTypes), set?.type)
          ),
          "id"
        ),
        plannerElementATLFilters: undefined,
        plannerElementNonAtlFilters: toPlannerElementNodeFilters(
          gradePlannerElementFilters
        ),
      },
      context: { useHttpLink: true },
    }),
    props({
      getGradePlannerElementNodeSets,
      ownProps: {
        isData,
        isLoading,
        nonPypNodeSetIdsLoading,
        interdisciplinaryCriteriaMYPLoading,
        plannerElementsLoading,
        subjectPlannerElementsLoading,
        subjectGradePlannerElementsLoading,
      },
    }) {
      const nodeSets = _.get(
        getGradePlannerElementNodeSets,
        "multiNode",
        BLANK_ARRAY
      );
      return {
        gradePlannerElements: mapNodeSetsToPlannerElementSets({ nodeSets }),
        isData: !_.isEmpty(nodeSets) && isData,
        isLoading:
          nonPypNodeSetIdsLoading ||
          interdisciplinaryCriteriaMYPLoading ||
          plannerElementsLoading ||
          subjectPlannerElementsLoading ||
          subjectGradePlannerElementsLoading ||
          _.includes(
            [1, 2],
            _.get(getGradePlannerElementNodeSets, "networkStatus")
          ) ||
          isLoading,
      };
    },
  }),
  graphql(getPlannerElementNodeSetIdsOfCurriculumQuery, {
    name: "getPypPlannerElementNodeSetIds",
    skip: ({
      curriculumId,
      selectFromAllPYP,
      curriculumType,
      taggableElements,
    }) =>
      !curriculumId ||
      !selectFromAllPYP ||
      curriculumType !== CURRICULUM_TYPE_PYP ||
      _.isEmpty(taggableElements),
    options: ({
      curriculumId,
      curriculumNodeInterfaceType,
      taggableElements,
      enableActioninPYP,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: curriculumId,
        type:
          curriculumNodeInterfaceType || NODE_TYPE_IB_PYP_CURRICULUM_PROGRAM,
        plannerElementNodeSetFilters: {
          types: getPypPlannerElementNodeSetTypes({
            taggableElements,
            enableActioninPYP,
          }),
        },
      },
    }),
    props({ getPypPlannerElementNodeSetIds, ownProps: { isLoading } }) {
      const nodeSets = _.map(
        _.get(
          getPypPlannerElementNodeSetIds,
          "node.plannerElementNodeSets.edges",
          BLANK_ARRAY
        ),
        "node"
      );
      return {
        pypPlannerElementNodeSets: nodeSets,
        pypNodeSetIdsLoading:
          _.includes(
            [1, 2],
            _.get(getPypPlannerElementNodeSetIds, "networkStatus")
          ) || isLoading,
      };
    },
  }),
  graphql(getPlannerElementNodeSetsFromIdsQuery, {
    name: "getPypPlannerElementNodeSets",
    skip: ({ pypPlannerElementNodeSets, selectFromAllPYP, curriculumType }) =>
      !selectFromAllPYP ||
      curriculumType !== CURRICULUM_TYPE_PYP ||
      _.isEmpty(pypPlannerElementNodeSets),
    options: ({ pypPlannerElementNodeSets, selectedGrades }) => {
      const grades = _.compact(selectedGrades);
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          plannerElementNodeSetIds: _.map(pypPlannerElementNodeSets, "id"),
          plannerElementATLFilters: !_.isEmpty(grades) ? { grades } : undefined,
          plannerElementNonAtlFilters: undefined,
        },
        context: {
          useHttpLink: true,
        },
      };
    },
    props({
      getPypPlannerElementNodeSets,
      ownProps: {
        isData,
        isLoading,
        pypPlannerElementNodeSets,
        pypNodeSetIdsLoading,
        enableActioninPYP,
        taggableElements,
        t,
      },
    }) {
      const data = _.get(
        getPypPlannerElementNodeSets,
        "multiNode",
        BLANK_ARRAY
      );
      const fields = [];
      _.forEach(taggableElements, element => {
        if (
          (element.value == "action" && !enableActioninPYP) ||
          element.value == "benchmarks"
        )
          return;

        const keyToFind =
          element.value == "concepts"
            ? ELEMENT_TYPE_PYP_KEY_CONCEPT
            : element.elementType;
        const dataElement = _.find(
          data,
          nodeSet => _.get(nodeSet, "type") == keyToFind
        );
        const nodes = mapNodeSetEdgesToNodes(
          _.get(
            dataElement,
            "plannerElementNodeSetWrapperV2.publishedData.edges",
            BLANK_ARRAY
          )
        );
        const fieldObj = {
          uid: _.get(element, "value"),
          resolvedMinimalTree: {
            [_.get(element, "value") == "lp"
              ? "learnerProfiles"
              : element?.value == "action" && enableActioninPYP
              ? "actions"
              : _.get(element, "value")]: nodes,
          },
        };
        fields.push(fieldObj);
      });
      if (!_.isEmpty(fields) && isData && !enableActioninPYP) {
        fields.push(getActionObj(t));
      }
      const hasAllPypData = _.size(data) === _.size(pypPlannerElementNodeSets);
      const isPypElementsStillLoading =
        pypNodeSetIdsLoading ||
        _.includes(
          [1, 2],
          _.get(getPypPlannerElementNodeSets, "networkStatus")
        );
      return {
        fields,
        isData: !_.isEmpty(data) && hasAllPypData && isData,
        isLoading: isPypElementsStillLoading || isLoading,
      };
    },
  }),
  graphql(getOrganizationSubjectsGradesAndTagsQuery, {
    name: "getOrganizationSubjectsGradesAndTags",
    options: ({ organizationId, curriculumId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        orgId: organizationId,
        curriculumProgramId: curriculumId,
        genericTagsFilter: {
          types: ["GENERIC"],
          ...getCurriculumIdsFilterForTags({
            curriculumProgramId: curriculumId,
          }),
        },
      },
    }),
    skip: ({ organizationId, curriculumId }) =>
      !organizationId || !curriculumId,
    props({
      getOrganizationSubjectsGradesAndTags: { node, networkStatus },
      ownProps: { isData, isLoading },
    }) {
      const subjects = node?.subjects || BLANK_ARRAY;
      const grades = node?.grades || BLANK_ARRAY;
      const tags = node?.genericTagsV2 || BLANK_ARRAY;
      return {
        subjects,
        grades,
        tags,
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getStandardSetsQuery, {
    name: "getStandardSets",
    options: ({
      searchText,
      curriculumProgramId,
      showCourseStandardSets,
      learningCourseId,
      isPYPCurriculum,
      orderBy,
      orderByDirection,
    }) => {
      const id = showCourseStandardSets
        ? learningCourseId
        : curriculumProgramId;

      const type = showCourseStandardSets
        ? "LEARNING_COURSE"
        : "CURRICULUM_PROGRAM";

      const filters = {
        searchText,
        archivalState: "ACTIVE",
      };

      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id,
          orderBy,
          orderByDirection,
          isPYPCurriculum,
          type,
          ...(type === "CURRICULUM_PROGRAM"
            ? { plannerElementNodeSetFilters: filters }
            : { plannerElementNodeFilters: filters }),
        },
      };
    },
    skip: ({ curriculumProgramId, learningCourseId, showCourseStandardSets }) =>
      showCourseStandardSets ? !learningCourseId : !curriculumProgramId,
    props({
      getStandardSets: { node, networkStatus },
      ownProps: {
        showCourseStandardSets,
        standardSetIdFromProps,
        selectFromAllPYP,
        postTaggedEntity,
        isPYPCurriculum,
        isLoading,
        fields,
      },
    }) {
      let standardSets = _.get(node, "plannerElementNodeSets.edges", []);
      if (showCourseStandardSets) {
        standardSets = isPYPCurriculum
          ? _.get(node, "benchmarksWrapper.standardsEdge", [])
          : _.get(node, "standardsWrapper.standardsEdge", []);
      }

      const effectiveStandardSetIdFromProps = !_.isEmpty(standardSetIdFromProps)
        ? standardSetIdFromProps
        : getStandardSetIds(fields, isPYPCurriculum);

      const updatedStandardSets =
        (!selectFromAllPYP || isUnitPlanOrAssessmentTagged(postTaggedEntity)) &&
        getIsNewGradingSetupEnabled()
          ? _.filter(standardSets, ({ standardSet }) =>
              _.includes(effectiveStandardSetIdFromProps, standardSet.id)
            )
          : standardSets;

      return {
        standardSets: getFilteredStandardSetsForTagging(updatedStandardSets),
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getLearningCourseStandardsQuery, {
    name: "getLearningCourseStandards",
    options: ({
      learningCourseId,
      standardSets,
      standardSetIdFromProps,
      selectFromAllPYP,
      ...rest
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: getPublishedSubjectStandardQueryVariables({
        ...rest,
        id: learningCourseId,
        standardSetIds:
          !selectFromAllPYP &&
          _.size(standardSetIdFromProps) &&
          getIsNewGradingSetupEnabled()
            ? standardSetIdFromProps
            : standardSets
            ? _.map(standardSets, "id")
            : [],
      }),
    }),
    skip: ({
      learningCourseId,
      showCourseStandards,
      isNewGradingSetupEnabled,
      standardSets,
    }) =>
      !learningCourseId ||
      !showCourseStandards ||
      !isNewGradingSetupEnabled ||
      _.isEmpty(standardSets),

    props({
      getLearningCourseStandards: { node, networkStatus },
      ownProps: { showCourseStandards, isLoading, ...rest },
    }) {
      const edges = getStandardEdges({
        node,
        showCourseStandards,
        selectFromAllPYP: _.get(rest, "selectFromAllPYP", false),
        leValues:
          getLeValues({
            fields: _.get(rest, "fields", []),
            fieldUid: STANDARD_UIDS[_.get(rest, "curriculumType")],
          }) || [],
      });
      return {
        standardSetMap: edges,
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  withLoader
)(TagLearningGoalsList);
